import { useMemo } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { countBy, orderBy, startCase } from 'lodash-es'
import { DateFormat } from 'constants/General'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { getLedgerWalletTransactions } from 'services/LedgerService'
import {
  FiatTransactionType,
  FiatTransactionTypes,
  LedgerWalletTransaction,
  TransactionSubTypeMapping,
  TransactionTypeMapping,
  TransactionTypes,
} from 'types/ledger/ledgerWalletTransactions'
import { paymentDirections } from 'types/types'
import { useWalletSearchQuery } from './useWalletSearchQuery'

const formatTransactionType = (type: string): string => {
  if (!type) return type
  if (TransactionTypeMapping[type]) return TransactionTypeMapping[type]
  if (type === type.toUpperCase()) return startCase(type.toLowerCase())
  return type
}

const mapLedgerTransactionsToSearchFormat = (ledgerTransactions: LedgerWalletTransaction[]) => {
  return ledgerTransactions?.map(transaction => {
    const payin =
      transaction?.type === TransactionTypes.PAYIN ||
      transaction?.type === TransactionTypes.PAYMENT_IN

    const direction = payin ? paymentDirections.IN : paymentDirections.OUT
    const isFiatTransaction = Object.values(FiatTransactionTypes).includes(
      transaction?.type as FiatTransactionType
    )

    return {
      id: transaction?.paymentId,
      transactionId: transaction?.transactionId,
      transactionUUID: transaction?.paymentId,
      hash: !isFiatTransaction
        ? transaction?.details?.hash || transaction?.transactionId
        : transaction?.transactionId,
      dateCreated: dayjs(transaction?.createdAt).format(DateFormat.D_MMM_YYYY_AT_HH_MM),

      amount: Math.abs(transaction?.amount?.value),
      fromAmount: transaction?.amount?.value,
      toAmount: transaction?.beneficiary?.amount?.value,
      fromCurrency: transaction?.amount?.currencyCode,
      toCurrency:
        transaction?.beneficiary?.amount?.currencyCode || transaction?.amount?.currencyCode,

      direction,
      status: transaction?.status?.toLowerCase(),
      type: formatTransactionType(transaction?.type),
      subtype: TransactionSubTypeMapping[transaction?.type] || '',
      description: formatTransactionType(transaction?.type),

      // TODO: Map customerReference and transactionReference to correct values
      paymentReference: transaction?.paymentReference,
      customerReference: transaction?.paymentReference,
      transactionReference: transaction?.transactionId,

      exchangeRate: transaction?.details?.exchangeRate,
      runningBalance: transaction?.runningBalance?.value,

      walletId: transaction?.walletId,
      walletIsid: transaction?.walletIsid,
      walletAddress: payin
        ? transaction?.beneficiary?.cryptoAddresses?.addresses?.[0]
        : transaction?.originator?.cryptoAddresses?.addresses?.[0],
      walletAddresses: payin
        ? transaction?.beneficiary?.cryptoAddresses?.addresses
        : transaction?.originator?.cryptoAddresses?.addresses,

      ...(isFiatTransaction &&
        payin && {
          accountNumber: transaction?.originator?.bankAccount?.accountNumber,
          bankCode: transaction?.originator?.bankAccount?.bankCode,
          bankName: transaction?.originator?.bankAccount?.bankName,
          name: transaction?.originator?.entity?.name,
        }),
      ...(isFiatTransaction &&
        !payin && {
          accountNumber: transaction?.beneficiary?.bankAccount?.accountNumber,
          bankCode: transaction?.beneficiary?.bankAccount?.bankCode,
          bankName: transaction?.beneficiary?.bankAccount?.bankName,
          name: transaction?.beneficiary?.entity?.name,
        }),

      originator: transaction?.originator,
      beneficiary: transaction?.beneficiary,
      details: transaction?.details,
    }
  })
}

export interface UseGetTransactionsHistoryParams {
  accountId?: number
  size: number | string
  page: number
  startDate?: Date
  endDate?: Date
  searchTerm?: string
  aggregation?: string[]
  isEnabled?: boolean
}

export const useGetTransactionsHistory = ({
  accountId,
  size,
  page = 0,
  startDate,
  endDate,
  searchTerm,
  aggregation,
  isEnabled = true,
}: UseGetTransactionsHistoryParams) => {
  const { enableTransactionStore } = useFeatureFlags()

  const { formattedStartDate, formattedEndDate } = useMemo(() => {
    let start: string | undefined
    let end: string | undefined

    if (startDate) {
      if (enableTransactionStore) {
        start = new Date(startDate).toISOString()
      } else {
        start = new Date(new Date(startDate).setUTCHours(0, 0, 0, 0)).toISOString()
      }
    }

    if (endDate) {
      if (enableTransactionStore) {
        end = new Date(endDate).toISOString()
      } else {
        end = new Date(new Date(endDate).setUTCHours(23, 59, 59, 999)).toISOString()
      }
    }

    return { formattedStartDate: start, formattedEndDate: end }
  }, [startDate, endDate, enableTransactionStore])

  const ledgerQuery = useQuery({
    enabled: enableTransactionStore && isEnabled,
    queryKey: reactQueryKeys.ledgerTransactionHistory({
      size: Number(size),
      page,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      searchTerm,
      aggregation,
    }),
    queryFn: () =>
      getLedgerWalletTransactions({
        size: Number(size),
        page,
        start: formattedStartDate,
        end: formattedEndDate,
      }),
    select: data => {
      if (!data) return

      const orderedTransactions = orderBy(data?.content || [], ['createdAt'], ['desc'])
      const transformedTransactions = mapLedgerTransactionsToSearchFormat(orderedTransactions)
      const typeCounts = countBy(transformedTransactions, transaction => transaction?.type)

      const aggregations = Object.entries(typeCounts).map(([type, count]) => ({
        value: type,
        label: `${type} (${count})`,
      }))

      return {
        transactions: {
          content: transformedTransactions,
          totalElements: data?.totalElements,
          pageable: {
            pageSize: Number(size),
            pageNumber: page,
          },
        },
        aggregations,
      }
    },
    placeholderData: keepPreviousData,
  })

  const ledgerQueryResult = useMemo(() => {
    return {
      ...ledgerQuery,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    }
  }, [ledgerQuery, formattedStartDate, formattedEndDate])

  const legacyWalletSearchQueryResult = useWalletSearchQuery({
    accountId,
    searchTerm,
    startDate,
    endDate,
    aggregation,
    pageSize: size,
    pageNumber: page,
  })

  return enableTransactionStore ? ledgerQueryResult : legacyWalletSearchQueryResult
}
